import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import Logo from "./Logo";

const links = [
  ["services", "nav_services"],
  ["work", "nav_work"],
  ["reviews", "nav_reviews"],
  ["about", "nav_about"],
  ["contact", "nav_contact"],
];

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const phone = content?.contact?.phone || "540-314-1957";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-obsidian/90 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
        <button onClick={() => go("top")} className="flex items-center gap-3" data-testid="nav-logo-btn">
          <Logo size={48} />
          <span className="hidden sm:block font-display text-lg leading-none tracking-wide text-white">
            ZURITA'S
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map(([id, key]) => (
            <button
              key={id}
              onClick={() => go(id)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-silver hover:text-acid transition-colors"
              data-testid={`nav-${id}`}
            >
              {t(key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="font-mono text-xs uppercase tracking-widest border border-white/20 px-3 py-2 text-white hover:border-acid hover:text-acid transition-colors"
            data-testid="lang-toggle"
          >
            {lang === "en" ? "EN" : "ES"} <span className="text-white/30">/</span> {lang === "en" ? "ES" : "EN"}
          </button>
          <a
            href={`tel:${phone.replace(/[^0-9]/g, "")}`}
            className="hidden md:inline-flex items-center gap-2 bg-acid px-4 py-2.5 font-heading font-semibold text-sm uppercase tracking-wide text-obsidian hover:bg-acid-hover transition-colors"
            data-testid="nav-call-btn"
          >
            <Phone size={16} /> {phone}
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-obsidian border-t border-white/10 px-5 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {links.map(([id, key]) => (
            <button key={id} onClick={() => go(id)} className="text-left font-heading uppercase text-xl text-white">
              {t(key)}
            </button>
          ))}
          <a href={`tel:${phone.replace(/[^0-9]/g, "")}`} className="mt-2 bg-acid text-obsidian text-center py-3 font-heading uppercase font-semibold">
            {phone}
          </a>
        </div>
      )}
    </motion.header>
  );
}
